import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Check, CalendarDays, Hourglass } from 'lucide-react'
import { Avatar } from './Avatar'
import { describeDueDate, type DueTone } from '../lib/dates'
import type { Card } from '../lib/types'

const DUE_TONES: Record<DueTone, string> = {
  overdue: 'bg-flame/10 text-flame',
  today: 'bg-ember-50 text-ember',
  soon: 'bg-cream-200 text-ink-soft',
  later: 'bg-cream-100 text-muted',
}

type Props = {
  card: Card
  onOpen: (card: Card) => void
  onToggleDone: (card: Card) => void
  /** Rendered inside the DragOverlay rather than in the column. */
  overlay?: boolean
}

export function CardTile({ card, onOpen, onToggleDone, overlay = false }: Props) {
  const due = describeDueDate(card.dueDate)

  return (
    <div
      data-draggable
      onClick={() => onOpen(card)}
      className={`group w-full rounded-xl bg-white p-3 text-left ring-1 ring-cream-200 transition
                  ${overlay ? 'rotate-2 shadow-lg ring-teal-300' : 'hover:ring-teal-300 hover:shadow-sm'}`}
    >
      <div className="flex items-start gap-2.5">
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            onToggleDone(card)
          }}
          aria-label={`Mark "${card.title}" done`}
          title="Mark done"
          /* Ticking a card clears it off the board, so the hover state is
             deliberately loud — fills green, grows, and throws a halo — to make
             a stray click obvious before it lands. A faint check appears on card
             hover so the target reads as a tick rather than an empty circle. */
          className="mt-px grid size-5 shrink-0 place-items-center rounded-full border-2
                     border-cream-200 text-transparent transition duration-150
                     group-hover:border-faint group-hover:text-cream-200
                     hover:scale-125 hover:border-grass hover:bg-grass hover:text-white
                     hover:ring-4 hover:ring-grass/25
                     focus-visible:scale-125 focus-visible:border-grass focus-visible:bg-grass
                     focus-visible:text-white focus-visible:ring-4 focus-visible:ring-grass/25"
        >
          <Check className="size-3" strokeWidth={3.5} />
        </button>

        {/* No pointerdown guard here — the title is most of the card's surface,
            so it has to stay grabbable for dragging. The sensor's activation
            distance keeps plain clicks working. */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onOpen(card)
          }}
          className="min-w-0 flex-1 text-left"
        >
          <span className="block text-sm leading-snug font-medium break-words text-ink">
            {card.title}
          </span>

          {/* One line only — the full note lives in the card detail panel. */}
          {card.notes && (
            <span className="mt-1 block truncate text-xs leading-snug text-muted">
              {card.notes}
            </span>
          )}
        </button>
      </div>

      <div className="mt-2.5 flex items-center gap-2 pl-7.5">
        <Avatar id={card.assignee} />

        {due && (
          <span
            className={`inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium ${DUE_TONES[due.tone]}`}
          >
            <CalendarDays className="size-3" />
            {due.label}
          </span>
        )}

        {/* Only meaningful while the card is parked in Waiting. The value is
            kept on the record either way, so moving out and back restores it. */}
        {card.status === 'waiting' && card.waitingOn && (
          <span
            title={`Waiting on ${card.waitingOn}`}
            className="inline-flex min-w-0 items-center gap-1 rounded-md bg-sand-50 px-1.5 py-0.5
                       text-xs font-medium text-ink-soft"
          >
            <Hourglass className="size-3 shrink-0" />
            <span className="truncate">{card.waitingOn}</span>
          </span>
        )}
      </div>
    </div>
  )
}

export function SortableCardTile(props: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.card.id,
  })

  return (
    <div
      ref={setNodeRef}
      data-card-id={props.card.id}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...attributes}
      {...listeners}
      className={isDragging ? 'opacity-30' : undefined}
    >
      <CardTile {...props} />
    </div>
  )
}
